"use client";
import React from "react";
import {
  Grid,
  Box,
  Typography,
  FormControl,
  Radio,
  Card,
  CardContent,
  Chip,
  Button,
} from "@mui/material";
import EditIcon from "@mui/icons-material/Edit";

/* ---------------------------------------------
   Modern SummaryRow component (clean + reusable)
---------------------------------------------- */
const SummaryRow = ({ label, value }) => (
  <Box
    sx={{
      display: "flex",
      justifyContent: "space-between",
      alignItems: "center",
    }}
  >
    <Typography variant="body2" sx={{ color: "#475569" }}>
      {label}
    </Typography>
    <Typography variant="body2" fontWeight={600} sx={{ color: "#1e293b" }}>
      {value}
    </Typography>
  </Box>
);

/* ---------------------------------------------
   Inner Plan Card (unchanged)
---------------------------------------------- */
const innerCardContent = ({ plan, selectedPlan, isCurrentPlan }) => {
  return (
    <CardContent
      sx={{
        display: "flex",
        alignItems: "center",
        justifyContent: "space-between",
      }}
    >
      <Box sx={{ display: "flex", alignItems: "center" }}>
        <Radio
          checked={selectedPlan === plan.plan_name}
          value={plan.plan_name}
        />
        <Box>
          <Typography variant="subtitle1" sx={{ fontWeight: 600 }}>
            {plan.plan_name}
          </Typography>
          <Typography variant="body2" color="text.secondary">
            ${plan.plan_amount} / month
          </Typography>
        </Box>
      </Box>

      {isCurrentPlan && (
        <Chip
          label="Current Plan"
          size="small"
          sx={{
            backgroundColor: "#e3f2fd",
            color: "#1976d2",
            fontWeight: 600,
          }}
        />
      )}
    </CardContent>
  );
};

/* ---------------------------------------------
   Main Component
---------------------------------------------- */
const SubscriptionTypeClean = ({
  numLocations = 1,
  selectedPlan,
  setSelectedPlanExternal,
  enableFreeTrial,
  setEnableFreeTrial,
  plansData = [],
  isEdit = false,
  onEditLocations,
  currentPlanData,
  subscriptionStatus,
  nextDueDate,

  // Token mode only
  isTokenMode = false,
  selectedTokenPack = null,
}) => {
  /* ------------------------------------------------------------
     TOKEN MODE (unchanged)
  ------------------------------------------------------------ */
  if (isTokenMode) {
    return (
      <Box sx={{ width: "100%", mt: 1 }}>
        <Box
          sx={{
            p: 3,
            backgroundColor: "#fff",
            border: "1px solid #e5e7eb",
            borderRadius: "7px",
          }}
        >
          <Typography
            variant="subtitle2"
            sx={{
              textAlign: "center",
              fontWeight: 600,
              fontSize: "14px",
              mb: 2,
              color: "black",
            }}
          >
            Token Purchase Summary
          </Typography>

          {!selectedTokenPack ? (
            <Typography
              variant="body2"
              color="text.secondary"
              sx={{ textAlign: "center" }}
            >
              Select a token pack to see summary →
            </Typography>
          ) : (
            <>
              <Box sx={{ textAlign: "center", mb: 3 }}>
                <Typography variant="h5" fontWeight={700}>
                  ${selectedTokenPack.amount}
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  One-Time Payment
                </Typography>
              </Box>

              <Box>
                <SummaryRow label="Selected Pack" value={selectedTokenPack.title} />
                <SummaryRow label="Total Tokens" value={selectedTokenPack.tokens} />

                <Box
                  sx={{
                    display: "flex",
                    justifyContent: "space-between",
                    mt: 2,
                    pt: 2,
                    borderTop: "1px solid #e5e7eb",
                  }}
                >
                  <Typography variant="body1" fontWeight={700}>
                    Total Payable
                  </Typography>
                  <Typography variant="body1" fontWeight={700}>
                    ${selectedTokenPack.amount}
                  </Typography>
                </Box>
              </Box>
            </>
          )}
        </Box>
      </Box>
    );
  }

  /* ------------------------------------------------------------
     REGULAR SUBSCRIPTION MODE
  ------------------------------------------------------------ */
  const selectedPlanObject =
    plansData?.find((p) => p.plan_name === selectedPlan) || null;

  return (
    <Box sx={{ width: "100%", mt: 3 }}>
      <Typography variant="h3" sx={{ fontSize: "18px", fontWeight: 700, mb: 2 }}>
        Choose Your Subscription
      </Typography>

      {/* Current Plan Section */}
      {isEdit && currentPlanData && (
        <Box
          sx={{
            p: 2.5,
            mb: 3,
            backgroundColor: "#f9fafb",
            borderRadius: "8px",
            border: "1px solid #e5e7eb",
          }}
        >
          <Box
            display="flex"
            justifyContent="space-between"
            alignItems="center"
            mb={2}
          >
            <Box>
              <Typography variant="subtitle1" fontWeight={600}>
                Current Subscription
              </Typography>
              <Typography variant="caption" color="text.secondary">
                {currentPlanData?.plan_name} — ${currentPlanData?.plan_amount}/mo
              </Typography>
            </Box>

            {subscriptionStatus && (
              <Chip
                label={
                  subscriptionStatus.charAt(0).toUpperCase() +
                  subscriptionStatus.slice(1)
                }
                size="small"
                color={
                  subscriptionStatus === "active"
                    ? "success"
                    : subscriptionStatus === "trialing"
                    ? "info"
                    : subscriptionStatus === "past_due"
                    ? "warning"
                    : "default"
                }
                sx={{ fontWeight: 600 }}
              />
            )}
          </Box>

          {/* Locations Box */}
          <Box
            sx={{
              p: 2,
              backgroundColor: "#fff",
              borderRadius: "6px",
              border: "1px solid #e0e0e0",
              display: "flex",
              justifyContent: "space-between",
            }}
          >
            <Box>
              <Typography variant="body2" fontWeight={600}>
                Current Locations
              </Typography>
              <Typography variant="caption">{numLocations} total</Typography>
            </Box>

            <Button
              variant="outlined"
              startIcon={<EditIcon />}
              onClick={onEditLocations}
              sx={{ textTransform: "none" }}
            >
              Edit Locations
            </Button>
          </Box>
        </Box>
      )}

      {/* Main Layout Grid */}
      <Grid container columnSpacing={2}>
        {/* ---- PLAN SELECTOR ---- */}
        <Grid size={{ xs: 12, md: 6 }}>
          <FormControl fullWidth>
            {plansData.map((plan) => {
              const isCurrentPlan =
                isEdit &&
                currentPlanData &&
                plan.plan_name === currentPlanData.plan_name;

              return (
                <Card
                  key={plan.plan_id}
                  onClick={() => setSelectedPlanExternal(plan)}
                  sx={{
                    mb: 2,
                    border:
                      selectedPlan === plan.plan_name
                        ? "2px solid var(--primaryColor)"
                        : "1px solid var(--borderColor)",
                    borderRadius: "7px",
                    cursor: "pointer",
                    transition: "0.2s",
                    "&:hover": {
                      boxShadow: "0 3px 10px rgba(0,0,0,0.06)",
                    },
                  }}
                >
                  {innerCardContent({
                    plan,
                    selectedPlan,
                    isCurrentPlan,
                  })}
                </Card>
              );
            })}
          </FormControl>
        </Grid>

        {/* ---- MODERN SUBSCRIPTION SUMMARY ---- */}
        <Grid size={{ xs: 12, md: 6 }}>
          <Box
            sx={{
              p: 3,
              background: "linear-gradient(to bottom right, #ffffff, #f8fafc)",
              border: "1px solid #e2e8f0",
              borderRadius: "12px",
              boxShadow: "0 2px 8px rgba(0,0,0,0.04)",
              transition: "0.2s ease",
              "&:hover": {
                boxShadow: "0 4px 14px rgba(0,0,0,0.06)",
              },
            }}
          >
            <Typography
              variant="subtitle2"
              fontWeight={700}
              mb={2}
              sx={{
                color: "#1e293b",
                letterSpacing: "0.3px",
                fontSize: "14px",
                textTransform: "uppercase",
              }}
            >
              Subscription Summary
            </Typography>

            {/* Summary Rows */}
            <Box sx={{ display: "flex", flexDirection: "column", gap: 1.3 }}>
              <SummaryRow label="Selected Plan" value={selectedPlan || "-"} />
              <SummaryRow
                label="Credits (tokens)"
                value={selectedPlanObject?.tokens || "-"}
              />
              <SummaryRow
                label="Price"
                value={
                  selectedPlanObject
                    ? `$${selectedPlanObject.plan_amount}`
                    : "-"
                }
              />

              {isEdit && nextDueDate && (
                <SummaryRow
                  label="Next Billing Date"
                  value={new Date(nextDueDate).toLocaleDateString("en-US", {
                    year: "numeric",
                    month: "short",
                    day: "numeric",
                  })}
                />
              )}

              {isEdit && subscriptionStatus && (
                <Box
                  sx={{
                    display: "flex",
                    justifyContent: "space-between",
                    alignItems: "center",
                    mt: 0.5,
                  }}
                >
                  <Typography variant="body2" sx={{ color: "#475569" }}>
                    Status
                  </Typography>

                  <Chip
                    label={
                      subscriptionStatus.charAt(0).toUpperCase() +
                      subscriptionStatus.slice(1)
                    }
                    size="small"
                    color={
                      subscriptionStatus === "active"
                        ? "success"
                        : subscriptionStatus === "trialing"
                        ? "info"
                        : subscriptionStatus === "past_due"
                        ? "warning"
                        : "default"
                    }
                    sx={{
                      fontWeight: 600,
                      borderRadius: "6px",
                      height: 24,
                    }}
                  />
                </Box>
              )}
            </Box>

            {/* Proration */}
            {selectedPlanObject &&
              Number(selectedPlanObject.prorationAmount) > 0 && (
                <Box
                  sx={{
                    mt: 2.5,
                    p: 2,
                    borderRadius: 2,
                    backgroundColor: "#fff7e6",
                    border: "1px solid #ffcc80",
                    boxShadow: "0 1px 4px rgba(255,165,0,0.15)",
                  }}
                >
                  <Typography
                    fontSize="14px"
                    fontWeight={600}
                    color="warning.dark"
                  >
                    Proration Due Today: $
                    {selectedPlanObject.prorationAmount}
                  </Typography>
                </Box>
              )}
          </Box>
        </Grid>
      </Grid>
    </Box>
  );
};

export default SubscriptionTypeClean;
